/**
 * Event types the timeline surfaces. The c_events route also returns visit events
 * (c_visit_event), which are not task assignments and carry no start/end window here — they must be
 * excluded both because they are out of scope and because their missing dates would otherwise corrupt
 * the chronological sort.
 */
const TASK_EVENT_TYPES = new Set(['c_scheduled_task_event', 'c_ad_hoc_task_event']);
const isTaskEvent = (event) => TASK_EVENT_TYPES.has(event.type) && !!event.c_start && !Number.isNaN(new Date(event.c_start).getTime());
/** The c_event id a notification is bound to, normalizing the reference/id shapes Cortex returns. */
export function notifEventId(notif) {
    const ref = notif.c_cortex_event;
    if (!ref) {
        return undefined;
    }
    return typeof ref === 'string' ? ref : ref._id;
}
export function mapNotifStatus(rawStatus) {
    switch (rawStatus) {
        case 'scheduled':
            return 'queued';
        case 'sent':
            return 'sent';
        case 'failed':
            return 'failed';
        // Canceled notifications never fire; they're filtered out upstream in buildTimeline so they don't
        // normally reach here. Mapping to a dedicated 'canceled' state (rather than the 'queued' default)
        // means that if one ever slips through it is labeled truthfully instead of looking still-pending.
        case 'canceled':
            return 'canceled';
        default:
            // Any unmapped status falls back to 'queued' (the safe, non-alarming default) instead of a
            // broken chip. Not logged: site_app has no structured logger, and a console warning here would
            // be per-row noise for what is a harmless display fallback.
            return 'queued';
    }
}
export function mapEventStatus(event) {
    if (event.c_completed) {
        return 'completed';
    }
    // An assignment's availability window is bounded by its end date (falling back to the start
    // when no end is set). While that window is still open — end date in the future — an incomplete
    // event is scheduled: it cannot be "missed" until the window has actually elapsed. This
    // intentionally takes precedence over the backend c_missed flag, which can be stale for
    // long-lived (far-future end) assignments.
    const windowEnd = new Date(event.c_end ?? event.c_start);
    if (windowEnd > new Date()) {
        return 'scheduled';
    }
    // Phase A: an event is only ever completed, missed, or scheduled. Once the window has elapsed
    // without completion (or the backend flagged it), it is missed.
    return 'missed';
}
/**
 * The instant a notification is displayed at: its actual send time when sent, otherwise its
 * scheduled send time. Mirrors the time the notification column renders (see use_assignment_columns)
 * so the chip/time pair a user sees is exactly what we sort on.
 */
const notificationDisplayTime = (notif) => new Date(notif.state === 'sent' && notif.sentAt ? notif.sentAt : notif.sendTime).getTime();
/** Order an event's notifications chronologically by their displayed time (ascending). */
const byDisplayTimeAsc = (a, b) => notificationDisplayTime(a) - notificationDisplayTime(b);
function mapNotif(notif, event) {
    const sends = notif.c_sent ?? [];
    const lastSent = sends.length ? sends[sends.length - 1] : undefined;
    // Recurring reminders keep c_status 'scheduled' and record each fire in c_sent instead of
    // flipping to 'sent', so treat any recorded send as sent.
    const hasSent = notif.c_status === 'sent' || !!lastSent;
    const state = hasSent ? 'sent' : mapNotifStatus(notif.c_status);
    // This occurrence's own send time; falls back to c_date only for rows that predate
    // c_baseline_date (it's a required field on current schemas, but not guaranteed backfilled on
    // pre-existing documents).
    const ownTime = notif.c_baseline_date ?? notif.c_date;
    const sentAt = hasSent ? (lastSent ?? ownTime) : undefined;
    // c_notif.c_tz is only backfilled lazily by the scheduler (on a reschedule, or when the
    // participant's account tz changes — see updateNotifsTZs in library.cs__notifications.js), so a
    // fresh or non-recurring notif frequently has none yet. Fall back to the event it's 1:1 joined to
    // via c_cortex_event, which carries the same participant-local zone (c_event.c_timezone) and is
    // already fetched — never fall through to the renderer's own viewer-tz default here, since that's
    // the site user's browser/profile zone, not the participant's.
    const timezone = notif.c_tz ?? event.c_timezone;
    return {
        name: notif.c_name,
        sendTime: ownTime,
        state,
        sentAt,
        timezone,
    };
}
export function buildTimeline(participantId, events, notifs, generating) {
    // Drop non-task events (e.g. visit events): they are out of scope and lack the start/end window the
    // timeline is built around. Removing them here also keeps the chronological sort well-defined.
    const taskEvents = events.filter(isTaskEvent);
    // Match the Site App activities-list ordering for consistency: chronological by start, then by the
    // task assignment's study-build order (c_task_assignment.c_order) so same-start events read in the
    // same, predictable order the Site App already uses elsewhere. Sort the raw events up front so the
    // subsequent map preserves that order.
    const orderedEvents = [...taskEvents].sort((a, b) => {
        const startDiff = new Date(a.c_start).getTime() - new Date(b.c_start).getTime();
        if (startDiff !== 0) {
            return startDiff;
        }
        return (a.c_task_assignment?.c_order ?? 0) - (b.c_task_assignment?.c_order ?? 0);
    });
    // Group notifications by the event they reference (c_cortex_event) in a single pass so the
    // per-event lookup below is O(1) rather than rescanning the whole notif list for every event
    // (a participant can have hundreds of events and 400+ notifications). Notifications are dropped
    // here when they:
    //   - are canceled (they will never fire — the task completed or a stop condition was met), or
    //   - don't reference any event id (nothing to attach them to).
    // Notifications whose event isn't in the fetched set simply never get looked up below.
    const notifsByEventId = new Map();
    for (const notif of notifs) {
        if (notif.c_status === 'canceled') {
            continue;
        }
        const eventId = notifEventId(notif);
        if (!eventId) {
            continue;
        }
        const bucket = notifsByEventId.get(eventId);
        if (bucket) {
            bucket.push(notif);
        }
        else {
            notifsByEventId.set(eventId, [notif]);
        }
    }
    // Event-centric: notifications that don't match any fetched event are intentionally dropped.
    // c_notif links to its event via c_cortex_event, so join on the event _id directly.
    const timelineEvents = orderedEvents.map((event) => {
        const matched = event._id ? (notifsByEventId.get(event._id) ?? []) : [];
        const id = event._id || event.c_hash || event.c_start;
        return {
            id,
            title: event.c_task?.c_name || event.type,
            type: event.type,
            start: event.c_start,
            end: event.c_end,
            timezone: event.c_timezone,
            status: mapEventStatus(event),
            hasNotificationConfigured: (event.c_task_assignment?.c_notifications?.length ?? 0) > 0,
            notifications: matched.map((notif) => mapNotif(notif, event)).sort(byDisplayTimeAsc),
        };
    });
    return { participantId, generating, events: timelineEvents };
}
